import React, { useState, useRef, useEffect } from 'react';
import { Mic, Square, Sparkles, Copy, FileText, Check, Info, Edit2, Wand2, User, Moon, Sun, GraduationCap, Activity, Zap } from 'lucide-react';
import { AudioRecorder } from '../services/audioUtils';
import { transcribeAudio, generateRadIA2Response } from '../services/geminiService';
import { isMobileDevice, streamTextToCloud } from '../services/db';
import { auth } from '../firebaseConfig';
import { ReportTemplate } from '../types';

interface Modular2ScreenProps {
  reports: ReportTemplate[];
}

const Modular2Screen: React.FC<Modular2ScreenProps> = ({ reports }) => {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('module_theme_modular2');
    if (saved) return saved === 'dark';
    return localStorage.getItem('app_theme') === 'dark';
  });

  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [lastCommand, setLastCommand] = useState('');
  const [aiMode, setAiMode] = useState<'free' | 'personalized'>('free');
  const [extension, setExtension] = useState<'Breve' | 'Medio' | 'Extenso'>('Medio');
  const [separation, setSeparation] = useState<'Párrafos' | 'Salto de Línea'>('Salto de Línea');
  const [anatomical, setAnatomical] = useState(false);
  const [technique, setTechnique] = useState(false);
  const [conclusion, setConclusion] = useState(true);
  const [differential, setDifferential] = useState(true);
  const [engineMode, setEngineMode] = useState<'phd' | 'gm1'>(() => {
    const saved = localStorage.getItem('modular2_engine_mode');
    return (saved === 'phd' || saved === 'gm1') ? saved as any : 'phd';
  });
  const [results, setResults] = useState<{findings: string; conclusion: string; differential: string;}>({ findings: '', conclusion: '', differential: '' });
  const [showInstructions, setShowInstructions] = useState(false);
  const [liveTranscript, setLiveTranscript] = useState('');

  const recorderRef = useRef<AudioRecorder | null>(null);
  const liveManagerRef = useRef<any>(null);
  const timerRef = useRef<any>(null);

  useEffect(() => {
    const bc = new BroadcastChannel('modular_sync_channel');
    bc.onmessage = (event) => {
      if (event.data.type === 'DRAFTING_SYNC_RESULTS' && event.data.results) {
        setResults({
          findings: event.data.results.findings || '',
          conclusion: event.data.results.conclusion || '',
          differential: event.data.results.differential || '',
        });
      }
    };
    return () => bc.close();
  }, []);

  useEffect(() => {
    const handleThemeChange = () => {
       const saved = localStorage.getItem('module_theme_modular2');
       if (!saved) setIsDarkMode(localStorage.getItem('app_theme') === 'dark');
    };
    window.addEventListener('app_theme_changed', handleThemeChange);
    return () => window.removeEventListener('app_theme_changed', handleThemeChange);
  }, []);

  useEffect(() => {
    localStorage.setItem('modular2_engine_mode', engineMode);
  }, [engineMode]);

  const toggleDarkMode = () => {
    const next = !isDarkMode;
    setIsDarkMode(next);
    localStorage.setItem('module_theme_modular2', next ? 'dark' : 'day');
    window.dispatchEvent(new Event('module_theme_changed'));
  };

  const [copiedSection, setCopiedSection] = useState<string | null>(null);

  useEffect(() => { return () => { if (timerRef.current) clearInterval(timerRef.current); }; }, []);
  const formatTime = (sec: number) => { const m = Math.floor(sec / 60); const s = sec % 60; return `${m}:${s < 10 ? '0' : ''}${s}`; };

  const toggleRecording = async () => {
    if (isRecording) {
      setIsRecording(false); 
      clearInterval(timerRef.current); 
      setRecordingTime(0);

      if (recorderRef.current) {
        setIsProcessing(true);
        try {
          const { blob, mimeType } = await recorderRef.current.stop();
          const transcript = await transcribeAudio(blob, mimeType, engineMode);
          const processedTranscript = transcript;
          setLastCommand(processedTranscript);
          if (!processedTranscript.trim()) { alert("No se ha detectado contenido en el dictado."); setIsProcessing(false); return; }
          
          if (isMobileDevice() && auth.currentUser) {
            streamTextToCloud(auth.currentUser.uid, processedTranscript, 'final');
          }
          
          const generated = await generateRadIA2Response(processedTranscript, reports, {
            extension,
            separation,
            technique,
            conclusion,
            differential
          });
          
          setResults({
             findings: generated.findings,
             conclusion: generated.conclusion,
             differential: generated.differential,
          });
        } catch (e: any) { 
          alert("No se ha podido procesar el dictado en este momento."); 
        } finally { 
          setIsProcessing(false); 
        }
      }
    } else {
      setLiveTranscript('');
      try { 
        recorderRef.current = new AudioRecorder(); 
        await recorderRef.current.start(); 
        setIsRecording(true); 
        timerRef.current = setInterval(() => setRecordingTime(prev => prev + 1), 1000); 
      } catch (e: any) { 
        alert("No se ha podido activar el micrófono. Verifique los permisos."); 
      }
    }
  };

  const handleCopy = (text: string, section: string) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopiedSection(section);
    setTimeout(() => setCopiedSection(null), 2000);
  };

  return (
    <div className={`flex flex-col h-full transition-colors ${isDarkMode ? 'bg-[#1e2024] text-gray-200' : 'bg-gray-50 text-gray-800'}`}>
      <div className={`border-b px-6 py-4 shadow-sm z-10 flex justify-between items-center ${isDarkMode ? 'bg-[#2a2d34] border-gray-700' : 'bg-white border-gray-200'}`}>
        <div className="flex items-center space-x-3">
           <div className="bg-blue-600 p-1.5 rounded-lg"><Sparkles size={18} className="text-white" /></div>
           <h1 className={`text-xl font-bold tracking-tight uppercase ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>MODULAR - 2</h1>
        </div>
        <div className="flex items-center space-x-2">
           <button onClick={toggleDarkMode} className={`p-2 rounded-lg border transition-all ${isDarkMode ? 'bg-gray-700 border-gray-600 text-yellow-400' : 'bg-white border-gray-200 text-gray-400'}`}>{isDarkMode ? <Sun size={18}/> : <Moon size={18}/>}</button>
           <button onClick={() => setShowInstructions(!showInstructions)} className={`transition-colors p-2 ${showInstructions ? 'text-blue-500' : 'text-gray-400 hover:text-blue-500'}`}><Info size={20} /></button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 pb-24">
        {showInstructions && (
          <div className={`border rounded-xl p-4 mb-6 animate-in fade-in slide-in-from-top-2 ${isDarkMode ? 'bg-blue-900/10 border-blue-900/20 text-blue-300' : 'bg-blue-50 border-blue-100 text-blue-700'}`}>
            <h3 className="font-bold text-sm mb-2 flex items-center"><Mic size={14} className="mr-1" /> Comandos de Voz Avanzados</h3>
            <p className="text-xs">Dicta los hallazgos directamente. La IA estructurará el informe según la configuración.</p>
          </div>
        )}

        <div className="flex flex-col items-center mb-6">
           <div className={`text-4xl font-mono font-bold mb-4 tabular-nums ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>{formatTime(recordingTime)}</div>
           <button onClick={toggleRecording} disabled={isProcessing} className={`w-20 h-20 rounded-full shadow-xl flex items-center justify-center transition-all transform active:scale-95 ${isRecording ? 'bg-red-500 animate-pulse ring-4 ring-red-200' : 'bg-blue-600 hover:bg-blue-700 ring-4 ring-blue-100'}`}>{isProcessing ? ( <div className="w-8 h-8 border-4 border-white border-t-transparent rounded-full animate-spin"></div> ) : isRecording ? ( <Square size={32} className="text-white" fill="currentColor" /> ) : ( <Mic size={36} className="text-white" /> )}</button>
           <p className="mt-3 text-xs font-medium text-gray-400 h-4 mb-6">{isRecording ? "Escuchando..." : isProcessing ? "Procesando..." : "Toca para dictar"}</p>

           <div className={`w-full max-w-2xl p-4 rounded-xl border shadow-sm animate-in fade-in slide-in-from-bottom-2 ${isDarkMode ? 'bg-blue-900/10 border-blue-900/20' : 'bg-blue-50/50 border-blue-100'}`}>
               <div className="flex items-center space-x-2 mb-3"><Wand2 size={14} className="text-blue-500" /><span className="text-xs font-bold text-blue-700 uppercase">Configuración (IA)</span></div>
               <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                  <div>
                    <label className="text-[10px] font-bold text-gray-500 uppercase block mb-1">Arquitectura</label>
                    <div className={`flex rounded-lg border p-0.5 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                      <button onClick={() => setEngineMode('phd')} className={`flex-1 py-1.5 text-[10px] font-bold rounded flex items-center justify-center ${engineMode === 'phd' ? 'bg-blue-600 text-white shadow-sm' : 'text-gray-500'}`}>
                        <GraduationCap size={12} className="mr-1" /> PHD
                      </button>
                      <button onClick={() => setEngineMode('gm1')} className={`flex-1 py-1.5 text-[10px] font-bold rounded flex items-center justify-center ${engineMode === 'gm1' ? 'bg-indigo-600 text-white shadow-sm' : 'text-gray-500'}`}>
                        <Activity size={12} className="mr-1" /> GM1
                      </button>
                    </div>
                  </div>
                  <div><label className="text-[10px] font-bold text-gray-500 uppercase block mb-1">Modo Generación</label><div className={`flex rounded-lg border p-0.5 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}><button onClick={() => setAiMode('free')} className={`flex-1 py-1.5 text-[10px] font-bold rounded flex items-center justify-center ${aiMode === 'free' ? 'bg-blue-600 text-white shadow-sm' : 'text-gray-500'}`}><Sparkles size={10} className="mr-1"/> Libre</button><button onClick={() => setAiMode('personalized')} className={`flex-1 py-1.5 text-[10px] font-bold rounded flex items-center justify-center ${aiMode === 'personalized' ? 'bg-blue-600 text-white shadow-sm' : 'text-gray-500'}`}><User size={10} className="mr-1"/> Personal</button></div></div>
                  <div><label className="text-[10px] font-bold text-gray-500 uppercase block mb-1">Extensión</label><div className={`flex rounded-lg border p-0.5 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>{(['Breve', 'Medio', 'Extenso'] as const).map(opt => (<button key={opt} onClick={() => setExtension(opt)} className={`flex-1 py-1.5 text-[10px] font-bold rounded ${extension === opt ? 'bg-blue-600 text-white shadow-sm' : 'text-gray-500'}`}>{opt}</button>))}</div></div>
                  <div><label className="text-[10px] font-bold text-gray-500 uppercase block mb-1">Separación</label><div className={`flex rounded-lg border p-0.5 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}><button onClick={() => setSeparation('Párrafos')} className={`flex-1 py-1.5 text-[10px] font-bold rounded ${separation === 'Párrafos' ? 'bg-blue-600 text-white shadow-sm' : 'text-gray-500'}`}>Párrafos</button><button onClick={() => setSeparation('Salto de Línea')} className={`flex-1 py-1.5 text-[10px] font-bold rounded ${separation === 'Salto de Línea' ? 'bg-blue-600 text-white shadow-sm' : 'text-gray-500'}`}>Líneas</button></div></div>
               </div>
               <div className="flex flex-wrap gap-3">
                   <button onClick={() => setAnatomical(!anatomical)} className={`flex items-center space-x-1 px-3 py-1.5 rounded-lg border text-[10px] font-bold transition-all ${anatomical ? 'bg-teal-500 text-white border-teal-600' : (isDarkMode ? 'bg-gray-800 border-gray-700 text-gray-500' : 'bg-white border-gray-200 text-gray-500')}`}>{anatomical && <Check size={12} />} <span>Anatómico</span></button>
                   <button onClick={() => setTechnique(!technique)} className={`flex items-center space-x-1 px-3 py-1.5 rounded-lg border text-[10px] font-bold transition-all ${technique ? 'bg-indigo-500 text-white border-indigo-600' : (isDarkMode ? 'bg-gray-800 border-gray-700 text-gray-500' : 'bg-white border-gray-200 text-gray-500')}`}>{technique && <Check size={12} />} <span>Técnica</span></button>
                   <button onClick={() => setConclusion(!conclusion)} className={`flex items-center space-x-1 px-3 py-1.5 rounded-lg border text-[10px] font-bold transition-all ${conclusion ? 'bg-emerald-500 text-white border-emerald-600' : (isDarkMode ? 'bg-gray-800 border-gray-700 text-gray-500' : 'bg-white border-gray-200 text-gray-500')}`}>{conclusion && <Check size={12} />} <span>Conclusión</span></button>
                   <button onClick={() => setDifferential(!differential)} className={`flex items-center space-x-1 px-3 py-1.5 rounded-lg border text-[10px] font-bold transition-all ${differential ? 'bg-amber-500 text-white border-amber-600' : (isDarkMode ? 'bg-gray-800 border-gray-700 text-gray-500' : 'bg-white border-gray-200 text-gray-500')}`}>{differential && <Check size={12} />} <span>Diferencial</span></button>
               </div>
            </div>
         </div>

        {lastCommand && ( <div className={`p-3 rounded-lg border shadow-sm mb-6 max-w-2xl mx-auto ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-100'}`}><span className="text-[10px] text-gray-400 uppercase font-bold block mb-1">Comando</span><p className="text-sm italic">"{lastCommand}"</p></div> )}

        {(results.findings || results.conclusion || results.differential) && (
          <div className="space-y-4 animate-in slide-in-from-bottom-4 duration-500 max-w-2xl mx-auto">
            <div className={`rounded-xl border shadow-sm overflow-hidden group transition-all hover:shadow-md ${isDarkMode ? 'bg-[#2a2d34] border-gray-700' : 'bg-white border-gray-200'}`}>
               <div className={`px-4 py-2 border-b flex justify-between items-center ${isDarkMode ? 'bg-gray-800/50 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
                  <div className="flex items-center space-x-2 text-blue-500 font-bold text-sm"><FileText size={16} /><span>HALLAZGOS</span></div>
                  <button onClick={(e) => { e.stopPropagation(); handleCopy(results.findings, 'findings'); }} className={`transition-all ${copiedSection === 'findings' ? 'text-green-500' : 'text-gray-400 hover:text-blue-500'}`}>
                    {copiedSection === 'findings' ? <Check size={16} /> : <Copy size={16} />}
                  </button>
               </div>
               <div 
                onClick={() => handleCopy(results.findings, 'findings')}
                className={`p-4 text-sm whitespace-pre-wrap leading-relaxed font-medium cursor-pointer transition-colors ${isDarkMode ? 'hover:bg-gray-800/50' : 'text-blue-700 hover:bg-blue-50/50'}`}
               >
                {results.findings}
               </div>
            </div>
            {results.conclusion && results.conclusion.trim() !== '' && (
              <div className={`rounded-xl border shadow-sm overflow-hidden group transition-all hover:shadow-md ${isDarkMode ? 'bg-[#2a2d34] border-gray-700' : 'bg-white border-gray-200'}`}>
                 <div className={`px-4 py-2 border-b flex justify-between items-center ${isDarkMode ? 'bg-gray-800/50 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
                    <div className="flex items-center space-x-2 text-emerald-500 font-bold text-sm"><Check size={16} /><span>CONCLUSIÓN</span></div>
                    <button onClick={(e) => { e.stopPropagation(); handleCopy(results.conclusion, 'conclusion'); }} className={`transition-all ${copiedSection === 'conclusion' ? 'text-green-500' : 'text-gray-400 hover:text-emerald-500'}`}>
                      {copiedSection === 'conclusion' ? <Check size={16} /> : <Copy size={16} />}
                    </button>
                 </div>
                 <div 
                  onClick={() => handleCopy(results.conclusion, 'conclusion')}
                  className={`p-4 text-sm whitespace-pre-wrap leading-relaxed font-medium cursor-pointer transition-colors ${isDarkMode ? 'hover:bg-gray-800/50' : 'hover:bg-gray-50'}`}
                 >
                  {results.conclusion}
                 </div>
              </div>
            )}
            {results.differential && results.differential.trim() !== '' && (
              <div className={`rounded-xl border shadow-sm overflow-hidden group transition-all hover:shadow-md ${isDarkMode ? 'bg-[#2a2d34] border-gray-700' : 'bg-white border-gray-200'}`}>
                 <div className={`px-4 py-2 border-b flex justify-between items-center ${isDarkMode ? 'bg-gray-800/50 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
                    <div className="flex items-center space-x-2 text-purple-500 font-bold text-sm"><Edit2 size={16} /><span>DIFERENCIALES</span></div>
                    <button onClick={(e) => { e.stopPropagation(); handleCopy(results.differential, 'differential'); }} className={`transition-all ${copiedSection === 'differential' ? 'text-green-500' : 'text-gray-400 hover:text-purple-500'}`}>
                      {copiedSection === 'differential' ? <Check size={16} /> : <Copy size={16} />}
                    </button>
                 </div>
                 <div 
                  onClick={() => handleCopy(results.differential, 'differential')}
                  className={`p-4 text-sm whitespace-pre-wrap leading-relaxed font-medium cursor-pointer transition-colors ${isDarkMode ? 'hover:bg-gray-800/50' : 'hover:bg-gray-50'}`}
                 >
                  {results.differential}
                 </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Modular2Screen;
